import React, {
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useFormik } from 'formik';
import CheckboxGroup from 'component/atom/forms/checkbox/CheckboxGroup';
import styles from './FieldForm.module.scss';
import { Agent } from 'hooks/endpoints';

const FieldForm = forwardRef(function FieldForm(
  {
    agentData,
    selectedFields,
    setSelectedFields,
    resetTrigger,
    onSubmitted,
    saveConnections,
    refetchAgentData,
    getCount,
  },
  ref
) {
  const didMountRef = useRef(false);
  const options = useMemo(() => {
    return (agentData || []).map((field) => ({
      label: field.displayName,
      value: field.fieldName,
      description: field.columnDescription,
      isChecked: field.isChecked,
    }));
  }, [agentData]);

  const initialFromAgent = useMemo(() => {
    return (agentData || []).filter((f) => f.isChecked).map((f) => f.fieldName);
  }, [agentData]);

  const formik = useFormik({
    initialValues: {
      fields: selectedFields ?? initialFromAgent,
    },
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const chosen = new Set(values.fields || []);
      const payload = (agentData || []).map((f) => ({
        id: f.id ?? f.fieldName,
        isChecked: chosen.has(f.fieldName),
      }));
      saveConnections(
        {
          url: Agent.SaveInnerConnectionDetails,
          data: payload,
          requireAuth: true,
        },
        {
          onSuccess: () => {
            setSubmitting(false);
            refetchAgentData();
            if (typeof onSubmitted === 'function') onSubmitted();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    },
  });

  useImperativeHandle(ref, () => ({
    submit: () => formik.handleSubmit(),
  }));

  useEffect(() => {
    if (!didMountRef.current) {
      if (!selectedFields || !selectedFields.length) {
        getCount(initialFromAgent);
      }
      didMountRef.current = true;
    }
  }, [initialFromAgent, selectedFields, getCount]);
  return (
    <form onSubmit={formik.handleSubmit} className={styles['field-form']}>
      <CheckboxGroup
        name="fields"
        options={options}
        selectedValues={formik.values.fields}
        onChange={(newValues) => {
          formik.setFieldValue('fields', newValues);
          setSelectedFields(newValues);
        }}
        error={formik.errors.fields}
        className={styles['checkbox-container']}
        column
        hasTag
        separators
        labelClassName={styles['checkbox-label']}
      />
    </form>
  );
});

export default FieldForm;
